import { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Download, MapPin } from "lucide-react";
import { Card, Info, Empty, sgn, NA } from "../ca/analysis/AnalysisUi";
import { eur0 } from "../ca/forecast/forecastUtils";
import { n0, n2, p1, s1, s2e, ZONE, HOW, dl } from "./produitsFmt";

const visible = (tree, open) => {
  const out = [];
  tree.forEach((dr) => { out.push([dr, 0]); if (!open[dr.key]) return; dr.children.forEach((sec) => { out.push([sec, 1]); if (open[sec.key]) sec.children.forEach((s) => out.push([s, 2])); }); });
  return out;
};
const flatten = (tree) => {
  const out = [];
  tree.forEach((dr) => { out.push(dr); dr.children.forEach((sec) => { out.push(sec); sec.children.forEach((s) => out.push(s)); }); });
  return out;
};

export default function GeoTree({ d, measure, mix, hideCosts, focus }) {
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (!focus) return;
    const o = {};
    d.tree.forEach((dr) => dr.children.forEach((sec) => { if (sec.children.some((s) => s.key === focus)) { o[dr.key] = true; o[sec.key] = true; } }));
    setOpen((x) => ({ ...x, ...o }));
    setTimeout(() => document.querySelector(`[data-testid="pa-tree-magasin-${focus}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 50);
  }, [focus, d]);
  const cols = [["ca", "CA HT", eur0], ["qte", "Quantités", n0], ["prix_moyen", "Prix moyen", n2]];
  if (!hideCosts) cols.push(["marge", "Marge €", eur0], ["taux_marge", "Taux", p1]);
  if (mix) cols.push(["mix", "Mix local", p1], ["var_ref", "vs réf.", s1], ["potentiel", "Potentiel", s2e]);
  else cols.push(["part_geo", "Part géo.", p1], ["evol_ca", "Évol. CA", s1]);
  const flat = flatten(d.tree);
  const right = (
    <div className="flex gap-2">
      <button onClick={() => setOpen(Object.fromEntries(flat.map((n) => [n.key, true])))} className="text-[11px] font-semibold text-blue-700 hover:underline" data-testid="pa-tree-expand">Tout déplier</button>
      <button onClick={() => setOpen({})} className="text-[11px] font-semibold text-slate-500 hover:underline" data-testid="pa-tree-collapse">Replier</button>
      <button onClick={() => dl("carte_produits.csv", [["level", "Niveau"], ["label", "Libellé"], ...cols.map(([k, l]) => [k, l])], flat)} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="pa-tree-export"><Download className="w-3.5 h-3.5" />CSV</button>
    </div>
  );
  return (
    <Card n="2" title="Tableau géographique DR → Secteur → Magasin" sub="Agrégats recalculés Σ/Σ à chaque niveau (aucune moyenne de taux ou de mix)." right={right} testid="pa-map-tree">
      <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="text-slate-500 border-b border-slate-200 text-left">
              <th className="py-1.5 pl-2 font-semibold">Zone</th>
              {cols.map(([k, l]) => <th key={k} className="py-1.5 px-1.5 text-right font-semibold">{l}{k === "var_ref" && <Info text={HOW.var} />}{k === "potentiel" && <Info text={HOW.potentiel} />}</th>)}
            </tr>
          </thead>
          <tbody>{visible(d.tree, open).map(([n, depth]) => <Row key={n.key} n={n} depth={depth} open={open} setOpen={setOpen} focus={focus} cols={cols} measure={measure} mix={mix} />)}</tbody>
        </table>
      </div>
      {d.tree.length === 0 && <Empty text="Aucun magasin sur ce périmètre." />}
    </Card>
  );
}

function Row({ n, depth, open, setOpen, focus, cols, measure, mix }) {
  const has = n.children?.length > 0;
  const isOpen = open[n.key];
  const foc = n.key === focus;
  const rowCls = `border-b border-slate-100 ${n.level === "dr" ? "bg-slate-50 font-bold" : n.level === "secteur" ? "font-semibold" : ""} ${foc ? "bg-blue-50 ring-1 ring-inset ring-blue-300" : "hover:bg-blue-50/40"}`;
  return (
      <tr className={rowCls} data-testid={`pa-tree-${n.level}-${n.level === "magasin" ? n.key : n.label}`}>
        <td className="py-1.5 whitespace-nowrap" style={{ paddingLeft: 8 + depth * 18 }}>
          {has ? (
            <button onClick={() => setOpen({ ...open, [n.key]: !isOpen })} className="inline-flex items-center gap-1" data-testid={`pa-tree-toggle-${n.label}`}>
              {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}{n.label}<span className="text-slate-400 font-normal ml-1">({n.n})</span>
            </button>
          ) : (
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{n.label}{mix && n.zone && <span className={`ml-1 px-1 py-0.5 rounded text-[9px] font-semibold ${ZONE[n.zone][1]}`}>{ZONE[n.zone][0]}</span>}</span>
          )}
        </td>
        {cols.map(([k, , f]) => <td key={k} className={`py-1.5 px-1.5 text-right tabular-nums ${["var_ref", "evol_ca", "potentiel"].includes(k) ? sgn(n[k]) : ""} ${k === measure ? "bg-blue-50/60" : ""}`}>{n[k] === null || n[k] === undefined ? NA : f(n[k])}</td>)}
      </tr>
  );
}
